
import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, Save, Trash2, Plus, AlertTriangle, FileJson, CheckCircle2, Loader2, X, Database, Search, Link as LinkIcon, ArrowLeft, ArrowRight, RefreshCw, ShieldCheck, Copy, Terminal, Edit2 } from 'lucide-react';
import { fetchDataSources, createDataSource, deleteDataSource, updateDataSource } from '../services/dataSourceService';
import { fetchDataCategories } from '../services/categoryService';
import { fetchAdminBoundaries, AdminBoundary } from '../services/geoService';
import { bulkInsertDataPoints, countUnembeddedPublicData, backfillPublicEmbeddings } from '../services/publicDataService';
import { fetchIndicators } from '../services/indicatorsService';
import { DraftDataPoint, DataSource, DataCategory, PublicDataPoint, Indicator } from '../types';

const DataInputView: React.FC = () => {
  // --- VIEW STATE ---
  const [viewMode, setViewMode] = useState<'SOURCES' | 'WORKBENCH'>('SOURCES');
  const [activeSource, setActiveSource] = useState<DataSource | null>(null);

  // --- SOURCES STATE ---
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form State (Create/Edit)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);
  const [isSubmittingSource, setIsSubmittingSource] = useState(false);
  
  const [sourceName, setSourceName] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  
  // Reference Data for Mapping
  const [categories, setCategories] = useState<DataCategory[]>([]);
  const [provinces, setProvinces] = useState<AdminBoundary[]>([]);
  const [indicators, setIndicators] = useState<Indicator[]>([]);

  // --- WORKBENCH STATE ---
  const [draftData, setDraftData] = useState<DraftDataPoint[]>([]);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- SEARCH-INDEX (EMBEDDING) HEALTH ---
  // Rows without an embedding are invisible to the AI oracle (retrieve_cambodia_data).
  const [unembedded, setUnembedded] = useState<number | null>(null);
  const [embedding, setEmbedding] = useState(false);
  const [embedMsg, setEmbedMsg] = useState('');

  const refreshEmbedHealth = async () => {
    try { setUnembedded(await countUnembeddedPublicData()); } catch { /* ignore */ }
  };

  const runBackfill = async () => {
    setEmbedding(true); setEmbedMsg(''); setErrorMsg(null);
    try {
      const { embedded, skipped, failures } = await backfillPublicEmbeddings(({ embedded, remaining }) => {
        setEmbedMsg(`Embedded ${embedded}… ${remaining} left`);
      });
      let msg = `Done — embedded ${embedded} point(s).`;
      if (skipped) msg += ` ${skipped} could not be embedded.`;
      if (failures && failures.length) msg += ` Reason: ${failures[0].reason}`;
      else msg += ' The oracle can now find them.';
      setEmbedMsg(msg);
      await refreshEmbedHealth();
    } catch (e: any) {
      setErrorMsg(e.message || 'Backfill failed. Ensure GEMINI_API_KEY is set on the function and you are a DATA_AGENT/CTO.');
    } finally {
      setEmbedding(false);
    }
  };

  // Constants
  const DEFAULT_CATEGORIES = categories.map(c => c.name);
  const DEFAULT_LOCATIONS = provinces.map(p => p.name_en);

  // --- INITIAL LOAD ---
  useEffect(() => {
    loadSources();
    loadReferenceData();
    refreshEmbedHealth();
  }, []);

  const loadSources = async () => {
    setLoadingSources(true);
    try {
      const data = await fetchDataSources();
      setSources(data);
    } catch (e) {
      console.error(e);
      // Maintain existing list on failure if available to prevent UI flash
      // setSources([]); 
    } finally {
      setLoadingSources(false);
    }
  };

  const loadReferenceData = async () => {
      try {
          const [cats, provs, inds] = await Promise.all([
              fetchDataCategories(),
              fetchAdminBoundaries(['COUNTRY', 'PROVINCE']),
              fetchIndicators().catch(() => [] as Indicator[]),
          ]);
          setCategories(cats);
          setProvinces(provs);
          setIndicators(inds);
      } catch (e) {
          console.error("Failed to load reference data", e);
      }
  };

  // --- SOURCE HANDLERS ---
  const handleSelectSource = (source: DataSource) => {
    setActiveSource(source);
    setViewMode('WORKBENCH');
    setDraftData([]);
    setJsonFile(null);
    setErrorMsg(null);
  };

  const openForm = (source?: DataSource) => {
      if (source) {
          setEditingSource(source);
          setSourceName(source.name);
          setSourceType(source.type);
          setSourceUrl(source.website_url || '');
      } else {
          setEditingSource(null);
          setSourceName('');
          setSourceType('');
          setSourceUrl('');
      }
      setIsFormOpen(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
      setIsFormOpen(false);
      setEditingSource(null);
      setSourceName('');
      setSourceType('');
      setSourceUrl('');
  };

  const handleSubmitSource = async () => {
    if (!sourceName || !sourceType) return;
    setIsSubmittingSource(true);
    try {
        if (editingSource) {
            // Update
            const updated = await updateDataSource(editingSource.id, {
                name: sourceName,
                type: sourceType,
                website_url: sourceUrl
            });
            setSources(prev => prev.map(s => s.id === updated.id ? updated : s));
            closeForm();
        } else {
            // Create
            const newSource = await createDataSource(sourceName, sourceType, sourceUrl);
            setSources([newSource, ...sources]);
            closeForm();
            // Automatically select and open workbench for new sources
            handleSelectSource(newSource);
        }
    } catch (e) {
        alert("Failed to save source. Check database permissions.");
    } finally {
        setIsSubmittingSource(false);
    }
  };

  const handleDeleteSource = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this source? This action cannot be undone.")) return;
    try {
        await deleteDataSource(id);
        setSources(prev => prev.filter(s => s.id !== id));
    } catch (e) {
        alert("Failed to delete source.");
    }
  };

  // --- JSON IMPORT HANDLERS ---
  
  const generatePrompt = () => {
      const categoryList = DEFAULT_CATEGORIES.join(', ');
      const locationList = DEFAULT_LOCATIONS.join(', ');
      const indicatorList = indicators.map(i => `${i.id} (${i.name_en}${i.canonical_unit ? `, ${i.canonical_unit}` : ''})`).join('; ');

      return `Act as a Data Extraction Specialist for the Cambodian Market.
Analyze the document/text I will provide and extract the quantitative data points into a JSON Array.

STRICT CONSTRAINTS:
1. "category": Must be one of: [${categoryList}]
2. "location": Must be one of: [${locationList}]. (Fix spelling if close, e.g., "Siemreap" -> "Siem Reap").
3. "indicator": OPTIONAL but PREFERRED. Pick the matching slug from: [${indicatorList || 'none configured'}]. Omit if none fits.
4. "period_start" / "period_end": "YYYY-MM-DD" range the data covers (e.g. year 2023 -> "2023-01-01" / "2023-12-31"). For a single point use the same date for both.
5. "period_granularity": one of "YEAR", "QUARTER", "MONTH", "DAY", "POINT".
6. "date_reported": "YYYY-MM-DD" (the reference date; usually period_end). If unknown use the document year.
7. "value_numeric": raw Number (no commas/symbols). If non-numeric, null and use "value_text". Use the indicator's unit when one is given.
8. "value_text": String for qualitative data.
9. "unit": String (e.g., "Tonnes", "USD", "Persons", "Hectares") — match the chosen indicator's unit.
10. "tags": Array of keyword Strings.
11. "summary_text": Short description (max 10 words).
12. "published_at": "YYYY-MM-DD" — the date the SOURCE published this figure, if stated (optional; distinct from the period it covers).
13. "confidence": Number 0..1 — your confidence the value is correct/exact (optional).

REQUIRED JSON OUTPUT FORMAT:
[
  {
    "category": "Agriculture",
    "location": "Battambang",
    "indicator": "rice_production",
    "period_start": "2023-01-01",
    "period_end": "2023-12-31",
    "period_granularity": "YEAR",
    "date_reported": "2023-12-31",
    "value_numeric": 50000,
    "value_text": null,
    "unit": "Tonnes",
    "tags": ["rice", "yield"],
    "summary_text": "Total Rice Production 2023",
    "published_at": "2024-03-15",
    "confidence": 0.9
  }
]

Please only output the raw JSON array, no markdown formatting.`;
  };

  const copyPrompt = () => {
      navigator.clipboard.writeText(generatePrompt());
      setSuccessMsg("Prompt copied to clipboard!");
      setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleJsonSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type !== "application/json" && !file.name.endsWith('.json')) {
          setErrorMsg("Invalid file type. Please upload a .json file.");
          return;
      }
      setJsonFile(file);
      setErrorMsg(null);
      parseJsonFile(file);
    }
  };

  const parseJsonFile = (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
          try {
              const text = e.target?.result as string;
              const json = JSON.parse(text);
              
              if (!Array.isArray(json)) {
                  throw new Error("JSON must be an array of objects.");
              }

              // Validate and Map fields. Carry the statistical-DB dimensions through
              // (indicator / period / published_at / confidence) so the new schema is
              // actually populated — previously these were dropped here and silently lost.
              const num = (v: any) => (typeof v === 'number' ? v : (v != null && v !== '' ? (parseFloat(v) || undefined) : undefined));
              const mappedData: DraftDataPoint[] = json.map((item: any, idx: number) => ({
                  id: `import-${Date.now()}-${idx}`,
                  category: item.category || 'Uncategorized',
                  location: item.location || 'Cambodia',
                  date_reported: item.date_reported || item.period_end || new Date().toISOString().split('T')[0],
                  value_numeric: num(item.value_numeric),
                  value_text: item.value_text || '',
                  unit: item.unit || 'Units',
                  tags: Array.isArray(item.tags) ? item.tags : [],
                  summary_text: item.summary_text || 'Imported Data',
                  confidence_warning: !item.category || !item.location,
                  // Statistical-DB dimensions
                  indicator: typeof item.indicator === 'string' ? item.indicator : undefined,
                  period_start: item.period_start || undefined,
                  period_end: item.period_end || undefined,
                  period_granularity: item.period_granularity || undefined,
                  published_at: item.published_at || undefined,
                  confidence: num(item.confidence),
              }));

              setDraftData(mappedData);
              setSuccessMsg(`Successfully imported ${mappedData.length} rows.`);
              setTimeout(() => setSuccessMsg(''), 3000);

          } catch (err: any) {
              console.error("JSON Parse Error", err);
              setErrorMsg("Failed to parse JSON. Ensure the format is correct. " + err.message);
              setDraftData([]);
          }
      };
      reader.readAsText(file);
  };

  const handleUpdateRow = (id: string, field: keyof DraftDataPoint, value: any) => {
    setDraftData(prev => prev.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const handleDeleteRow = (id: string) => {
    setDraftData(prev => prev.filter(row => row.id !== id));
  };

  const handleAddRow = () => {
    const newRow: DraftDataPoint = {
      id: `manual-${Date.now()}`,
      category: categories[0]?.name || 'Demographics',
      location: provinces[0]?.name_en || 'Phnom Penh',
      date_reported: new Date().toISOString().split('T')[0],
      value_numeric: 0,
      value_text: '',
      unit: 'Units',
      tags: [],
      summary_text: '',
      confidence_warning: false
    };
    setDraftData([...draftData, newRow]);
  };

  const handleSave = async () => {
    console.log(">>> Save Initiated");
    if (!activeSource) {
        setErrorMsg("No active data source selected.");
        return;
    }
    if (draftData.length === 0) {
        setErrorMsg("No data to save.");
        return;
    }
    
    // Safety check for reference data
    if (categories.length === 0 || provinces.length === 0) {
        setErrorMsg("System data (Categories/Locations) is still loading or failed. Please refresh the page.");
        loadReferenceData(); // Retry loading
        return;
    }

    setIsSaving(true);
    setErrorMsg(null);

    try {
        // 1. Prepare Payload & Validate on Client Side
        const payload: Partial<PublicDataPoint>[] = [];
        const invalidRows: { row: number, reason: string }[] = [];

        console.log(`Processing ${draftData.length} rows for commit...`);

        draftData.forEach((item, index) => {
            const cat = categories.find(c => c.name.toLowerCase() === item.category.trim().toLowerCase());
            const loc = provinces.find(p => p.name_en.toLowerCase() === item.location.trim().toLowerCase());

            if (!cat) {
                invalidRows.push({ row: index + 1, reason: `Invalid Category: ${item.category}` });
            }
            // Strict location check might be too harsh for manual entries, keeping it warning-level mostly but here strict.
            // if (!loc) invalidRows.push(...) 

            if (cat) {
                // Resolve optional indicator slug -> validated indicator_id.
                const ind = item.indicator ? indicators.find(i => i.id === item.indicator) : undefined;
                // Backfill period from the legacy single date if granular period absent.
                const periodStart = item.period_start || item.date_reported || undefined;
                const periodEnd = item.period_end || item.period_start || item.date_reported || undefined;

                payload.push({
                    source_id: activeSource.id,
                    category_id: cat.id,
                    location_id: loc ? loc.id : undefined, // Allow null location if not matched perfectly (or handle in backend)
                    date_reported: item.date_reported,
                    value_numeric: item.value_numeric,
                    value_text: item.value_text || undefined, // Use undefined for optional fields
                    unit: item.unit || ind?.canonical_unit, // fall back to the indicator's canonical unit
                    tags: item.tags,
                    summary_text: item.summary_text,
                    evidence_file_path: 'json_import',
                    // Statistical-DB dimensions (optional)
                    indicator_id: ind ? ind.id : undefined,
                    period_start: periodStart,
                    period_end: periodEnd,
                    period_granularity: item.period_granularity || (item.date_reported ? 'POINT' : undefined),
                    published_at: item.published_at,
                    confidence: item.confidence,
                });
            }
        });

        // 2. Prompt user if there are invalid rows
        if (invalidRows.length > 0) {
            console.warn("Found invalid rows during validation:", invalidRows.length);
            const confirmMsg = `${invalidRows.length} rows have invalid Categories and will be skipped:\n` + 
                               invalidRows.slice(0, 5).map(r => `- Row ${r.row}: ${r.reason}`).join('\n') + 
                               (invalidRows.length > 5 ? `\n...and ${invalidRows.length - 5} more.` : '') +
                               `\n\nDo you want to proceed with saving the ${payload.length} valid rows?`;
            
            if (!window.confirm(confirmMsg)) {
                console.log("User cancelled save operation.");
                setIsSaving(false);
                return;
            }
        }

        if (payload.length === 0) {
            setErrorMsg("No valid data points to save after validation.");
            setIsSaving(false);
            return;
        }

        console.log(`Sending ${payload.length} records to Supabase in batches...`);

        // 3. Insert into Supabase with Timeout Protection
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Database request timed out (120s). The operation might still be running in the background.")), 120000)
        );

        const result: any = await Promise.race([
            bulkInsertDataPoints(payload),
            timeoutPromise
        ]);
        
        console.log("Supabase operation complete.", result);

        // 4. Handle Result
        const insertedCount = result ? result.length : 0;
        const processedEstimate = payload.length; 

        let msg = `Success! Processed ${processedEstimate} records.`;
        if (insertedCount < processedEstimate) {
            msg += ` (${processedEstimate - insertedCount} potential duplicates skipped).`;
        }
        
        setSuccessMsg(msg);

        // Newly inserted rows need embedding before the oracle can find them — surface the count.
        refreshEmbedHealth();

        // Cleanup UI
        setTimeout(() => {
            setSuccessMsg('');
            setDraftData([]);
            setJsonFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }, 3000);

    } catch (e: any) {
        console.error("Save process failed:", e);
        setErrorMsg(`Database Error: ${e.message || "Failed to commit data."}`);
    } finally {
        setIsSaving(false);
    }
  };

  // --- RENDER: VIEW 1 (SOURCE LIST) ---
  if (viewMode === 'SOURCES') {
    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
             <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-slate-700 pb-6">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-white mb-2">Data Entry Workspace</h1>
                    <p className="text-slate-400">Manage Sources and Ingest Cambodia Public Data.</p>
                </div>
                <div className="flex gap-2">
                    {unembedded !== null && unembedded > 0 && (
                        <button
                            onClick={runBackfill}
                            disabled={embedding}
                            className="bg-amber-600/90 hover:bg-amber-500 text-white px-3 py-2.5 rounded-lg font-medium flex items-center gap-2 disabled:opacity-60 transition-colors"
                            title={`${unembedded} data point(s) are not in the AI search index. Click to embed them so the oracle can find them.`}
                        >
                            {embedding ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                            <span className="hidden sm:inline">{embedding ? 'Indexing…' : `Fix search index (${unembedded})`}</span>
                        </button>
                    )}
                    <button
                        onClick={loadSources}
                        className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2.5 rounded-lg border border-slate-700 transition-colors"
                        title="Refresh List"
                    >
                        <RefreshCw size={18} className={loadingSources ? 'animate-spin' : ''} />
                    </button>
                    <button 
                        onClick={() => openForm()}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 shadow-lg shadow-indigo-500/20 active:scale-95 transition-all"
                    >
                        <Plus size={18} /> New Data Source
                    </button>
                </div>
            </div>

            {/* Search-index (embedding) status — explains why the oracle may not find data. */}
            {embedMsg && (
                <div className="bg-indigo-900/20 border border-indigo-500/40 text-indigo-100 p-3 rounded-lg flex items-center gap-3 text-sm">
                    {embedding ? <Loader2 size={16} className="animate-spin shrink-0 text-indigo-300" /> : <CheckCircle2 size={16} className="shrink-0 text-emerald-400" />}
                    <span className="flex-1">{embedMsg}</span>
                    {!embedding && <button onClick={() => setEmbedMsg('')} className="text-indigo-300 hover:text-white"><X size={15} /></button>}
                </div>
            )}

            {isFormOpen && (
                <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl mb-8 animate-in slide-in-from-top-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-white">{editingSource ? 'Edit Source' : 'Register New Source'}</h3>
                        <button onClick={closeForm} className="text-slate-500 hover:text-white"><X size={20}/></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Source Name *</label>
                            <input 
                                autoFocus
                                value={sourceName}
                                onChange={e => setSourceName(e.target.value)}
                                placeholder="e.g. Annual Rice Production Report 2024" 
                                className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Source Type *</label>
                            <select 
                                value={sourceType}
                                onChange={e => setSourceType(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                            >
                                <option value="" disabled>Select Type</option>
                                <option value="GOV">Government</option>
                                <option value="INTL_ORG">International Org / NGO</option>
                                <option value="PRIVATE_FIRM">Private Firm</option>
                                <option value="NEWS">News Media</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Website URL (Optional)</label>
                            <input 
                                value={sourceUrl}
                                onChange={e => setSourceUrl(e.target.value)}
                                placeholder="https://..." 
                                className="w-full bg-slate-900 border border-slate-600 rounded p-2.5 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button onClick={closeForm} className="text-slate-400 hover:text-white px-4 py-2">Cancel</button>
                        <button 
                            onClick={handleSubmitSource} 
                            disabled={!sourceName || !sourceType || isSubmittingSource}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmittingSource && <Loader2 className="animate-spin" size={16} />}
                            {editingSource ? 'Update Source' : 'Create & Open'}
                        </button>
                    </div>
                </div>
            )}

            <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input 
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search existing data sources..." 
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl py-4 pl-10 pr-4 text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
            </div>

            {loadingSources ? (
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="animate-spin text-indigo-500 mb-4" size={32} />
                    <p className="text-slate-500">Loading sources...</p>
                </div>
            ) : sources.length === 0 ? (
                <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700 border-dashed">
                    <Database size={48} className="mx-auto text-slate-600 mb-4" />
                    <h3 className="text-lg font-medium text-slate-300">No data sources found</h3>
                    <p className="text-slate-500 mb-6">Create your first data source to start ingesting data.</p>
                    <button 
                        onClick={() => openForm()}
                        className="text-indigo-400 hover:text-indigo-300 font-medium"
                    >
                        Create New Source
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sources.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase())).map(source => (
                        <div 
                            key={source.id} 
                            onClick={() => handleSelectSource(source)}
                            className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-indigo-500 hover:shadow-lg hover:shadow-indigo-500/10 cursor-pointer transition-all group relative overflow-hidden flex flex-col h-full"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex gap-2">
                                <button 
                                    onClick={(e) => { e.stopPropagation(); openForm(source); }}
                                    className="text-slate-400 hover:text-indigo-400 p-1.5 rounded hover:bg-slate-700/50"
                                    title="Edit Source"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button 
                                    onClick={(e) => handleDeleteSource(e, source.id)}
                                    className="text-slate-400 hover:text-red-400 p-1.5 rounded hover:bg-slate-700/50"
                                    title="Delete Source"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            
                            <div className="mb-4 flex-1">
                                <div className="flex justify-between items-start">
                                    <div className="w-12 h-12 bg-slate-900 rounded-lg border border-slate-600 flex items-center justify-center mb-3 group-hover:border-indigo-500/50 transition-colors">
                                        <Database className="text-indigo-500" size={24} />
                                    </div>
                                    <ArrowRight className="text-slate-600 group-hover:text-indigo-400 transition-colors mt-2" size={20}/>
                                </div>
                                <h3 className="text-lg font-bold text-white leading-tight mb-1 line-clamp-2" title={source.name}>{source.name}</h3>
                                <p className="text-slate-400 text-sm line-clamp-1">{source.type}</p>
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-700 pt-4 mt-2">
                                 <span className="flex items-center gap-1">
                                    {source.website_url ? <LinkIcon size={12}/> : <ShieldCheck size={12} />}
                                    <span className={source.credibility > 80 ? 'text-emerald-400' : 'text-slate-400'}>
                                        {source.credibility}% Trust
                                    </span>
                                 </span>
                                 <span>{new Date(source.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
  }

  // --- RENDER: VIEW 2 (WORKBENCH) ---
  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in slide-in-from-right duration-300">
      {/* Workbench Header */}
      <div className="flex items-center justify-between border-b border-slate-700 pb-4">
          <div className="flex items-center gap-4">
              <button onClick={() => setViewMode('SOURCES')} className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 transition-all">
                  <ArrowLeft size={20} />
              </button>
              <div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                      <Database size={12} />
                      <span className="uppercase font-bold tracking-wider">Active Source</span>
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-white">{activeSource?.name}</h1>
              </div>
          </div>
          <div className="flex gap-2">
              <div className="hidden md:flex flex-col items-end text-right">
                  <span className="text-xs text-slate-500">Type</span>
                  <span className="text-sm text-slate-300">{activeSource?.type}</span>
              </div>
          </div>
      </div>

      <div className={`grid grid-cols-1 ${draftData.length > 0 ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-8 transition-all`}>
        
        {/* LEFT: PROMPT GENERATOR */}
        <div className={`bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg ${draftData.length > 0 ? 'hidden md:block' : ''}`}>
           <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
             <Terminal size={20} className="text-indigo-400" />
             1. External Extraction
           </h2>
           <p className="text-slate-400 text-sm mb-4">
               Use Gemini, ChatGPT, or Grok to extract data from your documents. Copy this prompt to ensure the output matches our database format.
           </p>
           
           <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 relative group">
               <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap max-h-60 overflow-y-auto custom-scrollbar">
                   {generatePrompt()}
               </pre>
               <button 
                  onClick={copyPrompt}
                  className="absolute top-2 right-2 bg-slate-800 hover:bg-slate-700 text-white p-2 rounded-md border border-slate-700 shadow-lg transition-all opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                  title="Copy Prompt"
               >
                   <Copy size={14} />
               </button>
           </div>
           
           <div className="mt-4 flex gap-2 justify-center">
                <a href="https://gemini.google.com/" target="_blank" rel="noreferrer" className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-full transition-colors">Open Gemini</a>
                <a href="https://chatgpt.com/" target="_blank" rel="noreferrer" className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-full transition-colors">Open ChatGPT</a>
           </div>
        </div>

        {/* RIGHT: JSON IMPORT */}
        <div className={`bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-lg`}>
           <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
             <UploadCloud size={20} className="text-emerald-400" />
             2. Import JSON
           </h2>
           <p className="text-slate-400 text-sm mb-4">
               Upload the <code>.json</code> file generated by the AI to verify and commit the data points.
           </p>
           
           {/* Error Box */}
           {errorMsg && (
               <div className="bg-red-900/20 border border-red-500/50 text-red-200 p-4 rounded-lg flex items-center gap-3 mb-4 animate-in shake">
                   <AlertTriangle className="shrink-0 text-red-400" />
                   <div className="flex-1">
                       <p className="font-bold text-sm">Action Failed</p>
                       <p className="text-xs font-mono mt-1 break-words">{errorMsg}</p>
                   </div>
                   <button onClick={() => setErrorMsg(null)} className="text-red-400 hover:text-white"><X size={16}/></button>
               </div>
           )}

           <div className="space-y-4">
             {/* File Drop / Selection Area */}
             <div 
               onClick={() => fileInputRef.current?.click()}
               className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer group relative ${jsonFile ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:bg-slate-700/30'}`}
             >
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleJsonSelect} 
                    className="hidden" 
                    accept=".json,application/json"
                />
                
                {jsonFile ? (
                    <div className="flex flex-col items-center animate-in fade-in duration-300">
                        <FileJson className="text-emerald-400 mb-2" size={32} />
                        <p className="text-sm text-emerald-200 font-medium truncate max-w-full px-4">{jsonFile.name}</p>
                        <p className="text-xs text-emerald-400 mt-1">{(jsonFile.size / 1024).toFixed(2)} KB</p>
                         <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setJsonFile(null);
                                setDraftData([]);
                                if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="mt-3 flex items-center gap-1 text-xs bg-slate-900 text-slate-300 px-3 py-1.5 rounded hover:bg-red-900/50 hover:text-red-300 border border-slate-700 transition-colors"
                         >
                            <X size={12} /> Remove
                         </button>
                    </div>
                ) : (
                    <>
                        <FileJson className="mx-auto text-slate-500 group-hover:text-emerald-400 mb-2 transition-colors" size={32} />
                        <p className="text-sm text-slate-400 font-medium">Drop JSON File Here</p>
                        <p className="text-xs text-slate-600 mt-1">Click to browse</p>
                    </>
                )}
             </div>
           </div>
        </div>

        {/* STAGING AREA SECTION (Visible only when data exists) */}
        {draftData.length > 0 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 lg:col-span-2">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <CheckCircle2 size={24} className="text-emerald-400" />
                        3. Review & Commit
                        <span className="text-sm font-normal text-slate-400 ml-2 bg-slate-800 px-2 py-0.5 rounded-full border border-slate-700">
                            {draftData.length} records found
                        </span>
                    </h2>
                    <button 
                        onClick={handleAddRow}
                        className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 font-medium"
                    >
                        <Plus size={16} /> Add Manual Row
                    </button>
                </div>

                <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto max-h-[500px]">
                        <table className="w-full text-left text-sm relative">
                            <thead className="bg-slate-900 text-slate-400 uppercase tracking-wider font-semibold sticky top-0 z-10 shadow-md">
                                <tr>
                                    <th className="px-4 py-3 bg-slate-900">Location (Prov.)</th>
                                    <th className="px-4 py-3 bg-slate-900">Category</th>
                                    <th className="px-4 py-3 bg-slate-900 w-24">Num. Value</th>
                                    <th className="px-4 py-3 bg-slate-900 w-32">Text Value</th>
                                    <th className="px-4 py-3 bg-slate-900 w-20">Unit</th>
                                    <th className="px-4 py-3 bg-slate-900 w-28">Date</th>
                                    <th className="px-4 py-3 bg-slate-900 w-32">Tags</th>
                                    <th className="px-4 py-3 bg-slate-900">Summary / Context</th>
                                    <th className="px-4 py-3 w-10 bg-slate-900"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {draftData.map((row) => (
                                    <tr key={row.id} className={`group hover:bg-slate-700/30 transition-colors ${row.confidence_warning ? 'bg-yellow-900/10' : ''}`}>
                                        <td className="px-4 py-2">
                                            <select 
                                                value={row.location} 
                                                onChange={(e) => handleUpdateRow(row.id, 'location', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-200 outline-none w-32"
                                            >
                                                {DEFAULT_LOCATIONS.map(l => <option key={l} value={l} className="bg-slate-800">{l}</option>)}
                                                {!DEFAULT_LOCATIONS.includes(row.location) && <option value={row.location} className="bg-slate-800">{row.location} (Unknown)</option>}
                                            </select>
                                        </td>
                                        <td className="px-4 py-2">
                                            <select 
                                                value={row.category} 
                                                onChange={(e) => handleUpdateRow(row.id, 'category', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-indigo-300 font-medium outline-none w-32"
                                            >
                                                {DEFAULT_CATEGORIES.map(c => <option key={c} value={c} className="bg-slate-800">{c}</option>)}
                                                {!DEFAULT_CATEGORIES.includes(row.category) && <option value={row.category} className="bg-slate-800">{row.category} (Invalid)</option>}
                                            </select>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="relative">
                                                <input 
                                                    type="number"
                                                    value={row.value_numeric ?? ''}
                                                    onChange={(e) => handleUpdateRow(row.id, 'value_numeric', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                                    className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-white font-mono w-24 outline-none placeholder-slate-600"
                                                    placeholder="-"
                                                />
                                                {row.confidence_warning && (
                                                    <AlertTriangle size={12} className="text-yellow-500 absolute top-1/2 -translate-y-1/2 right-0" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text"
                                                value={row.value_text || ''}
                                                onChange={(e) => handleUpdateRow(row.id, 'value_text', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-300 text-xs w-full min-w-[100px] outline-none placeholder-slate-600"
                                                placeholder="Optional Text"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text"
                                                value={row.unit}
                                                onChange={(e) => handleUpdateRow(row.id, 'unit', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-400 w-20 outline-none"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="date"
                                                value={row.date_reported}
                                                onChange={(e) => handleUpdateRow(row.id, 'date_reported', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-300 text-xs w-32 outline-none [color-scheme:dark]"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text"
                                                value={Array.isArray(row.tags) ? row.tags.join(', ') : ''}
                                                onChange={(e) => handleUpdateRow(row.id, 'tags', e.target.value.split(',').map(t=>t.trim()).filter(t=>t))}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-400 text-xs w-32 outline-none truncate focus:text-clip"
                                                placeholder="comma, tags"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input 
                                                type="text"
                                                value={row.summary_text}
                                                onChange={(e) => handleUpdateRow(row.id, 'summary_text', e.target.value)}
                                                className="bg-transparent border border-transparent hover:border-slate-600 focus:border-indigo-500 rounded px-2 py-1 text-slate-400 w-full min-w-[200px] outline-none truncate focus:text-clip"
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <button 
                                                onClick={() => handleDeleteRow(row.id)}
                                                className="text-slate-600 hover:text-red-400 p-1 rounded transition-colors"
                                                title="Remove Row"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Action Bar */}
                    <div className="bg-slate-900 px-6 py-4 border-t border-slate-800 flex justify-between items-center">
                        <div className="text-xs text-slate-500 hidden md:block">
                            * Verified against <span className="text-indigo-400 font-bold">{activeSource?.name}</span>.
                        </div>
                        <div className="flex gap-4 w-full md:w-auto">
                            <button 
                                onClick={() => {
                                    setDraftData([]);
                                    setJsonFile(null);
                                    setErrorMsg(null);
                                }}
                                className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                            >
                                Discard All
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold shadow-lg shadow-emerald-900/20 active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18} />} 
                                Commit {draftData.length} Records
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
      </div>
      
      {/* Success Notification */}
      {successMsg && (
          <div className="fixed bottom-24 md:bottom-8 right-8 bg-emerald-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-in slide-in-from-right duration-300 z-50">
              <CheckCircle2 size={24} />
              <div>
                  <h4 className="font-bold">Success!</h4>
                  <p className="text-xs text-emerald-100">{successMsg}</p>
              </div>
          </div>
      )}
    </div>
  );
};

export default DataInputView;
